/**
 * Subscription Modal Component
 * Allows users to subscribe or manage their subscription
 */

import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { XMarkIcon, CheckIcon, SparklesIcon } from '@heroicons/react/24/outline'
import { CheckBadgeIcon } from '@heroicons/react/24/solid'
import { motion, AnimatePresence } from 'framer-motion'
import { subscriptionsAPI, SubscriptionInfo, SubscriptionPlan } from '@api/subscriptions'
import { SubscriptionBadge } from './SubscriptionBadge'
import toast from 'react-hot-toast'
import clsx from 'clsx'

interface SubscriptionModalProps {
  isOpen: boolean
  onClose: () => void
  currentSubscription?: SubscriptionInfo
}

export const SubscriptionModal: React.FC<SubscriptionModalProps> = ({
  isOpen,
  onClose,
  currentSubscription,
}) => {
  const [selectedPlan, setSelectedPlan] = useState<'student' | 'researcher' | null>(null)
  const queryClient = useQueryClient()

  const { data: subscriptionInfo, refetch: refetchSubscription } = useQuery<SubscriptionInfo>({
    queryKey: ['subscription', 'info'],
    queryFn: () => subscriptionsAPI.getInfo(),
    enabled: isOpen,
  })

  const { data: plans, isLoading: isLoadingPlans } = useQuery<Record<string, SubscriptionPlan>>({
    queryKey: ['subscription', 'plans'],
    queryFn: () => subscriptionsAPI.getPlans(),
    enabled: isOpen,
  })

  const createSubscriptionMutation = useMutation({
    mutationFn: (subscriptionType: 'student' | 'researcher') =>
      subscriptionsAPI.createSubscription({
        subscription_type: subscriptionType,
        // Simulated payment - in production, this would come from payment processor
        transaction_id: `sim_${Date.now()}`,
        amount_paid: plans?.[subscriptionType]?.price_usd,
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] })
      queryClient.invalidateQueries({ queryKey: ['credit-balance'] })
      refetchSubscription()
      setSelectedPlan(null)
      onClose()
    },
    onError: (error: any) => {
    },
  })

  const cancelSubscriptionMutation = useMutation({
    mutationFn: () => subscriptionsAPI.cancelSubscription(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] })
      queryClient.invalidateQueries({ queryKey: ['credit-balance'] })
      refetchSubscription()
      onClose()
    },
    onError: (error: any) => {
    },
  })

  const currentSub = subscriptionInfo || currentSubscription
  const hasActiveSubscription = currentSub && currentSub.subscription_type !== 'free' && currentSub.is_active

  const handleSubscribe = () => {
    if (!selectedPlan) {
      return
    }
    createSubscriptionMutation.mutate(selectedPlan)
  }

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex min-h-screen items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black bg-opacity-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
          >
            {/* Header */}
            <div className="sticky top-0 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Subscription Plans</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  {hasActiveSubscription
                    ? 'Manage your subscription or upgrade to a higher tier'
                    : 'Choose a plan that fits your research needs'}
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Current Subscription */}
              {currentSub && (
                <div className="mb-6 p-4 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-600 dark:text-slate-400">Current Plan</p>
                      <div className="flex items-center gap-2 mt-1">
                        <SubscriptionBadge
                          subscriptionType={currentSub.subscription_type}
                          planName={currentSub.plan_name}
                          isActive={currentSub.is_active}
                          size="md"
                        />
                        {currentSub.next_billing_date && (
                          <span className="text-sm text-slate-500 dark:text-slate-400">
                            Renews: {new Date(currentSub.next_billing_date).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                    {hasActiveSubscription && (
                      <button
                        onClick={() => cancelSubscriptionMutation.mutate()}
                        disabled={cancelSubscriptionMutation.isPending}
                        className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                      >
                        {cancelSubscriptionMutation.isPending ? 'Cancelling...' : 'Cancel Subscription'}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Plans */}
              {isLoadingPlans ? (
                <div className="text-center py-12">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
                  <p className="mt-4 text-slate-600 dark:text-slate-400">Loading plans...</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {plans &&
                    Object.entries(plans)
                      .filter(([key]) => key !== 'free')
                      .map(([key, plan]) => {
                        const isSelected = selectedPlan === key
                        const isCurrent = currentSub?.subscription_type === key
                        const isUpgrade =
                          currentSub &&
                          currentSub.subscription_type !== 'free' &&
                          key === 'researcher' &&
                          currentSub.subscription_type === 'student'

                        return (
                          <div
                            key={key}
                            className={clsx(
                              'relative p-6 rounded-xl border-2 transition-all cursor-pointer',
                              isSelected
                                ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20'
                                : isCurrent
                                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                            )}
                            onClick={() => !isCurrent && setSelectedPlan(key as 'student' | 'researcher')}
                          >
                            {isCurrent && (
                              <div className="absolute top-4 right-4">
                                <CheckBadgeIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                              </div>
                            )}
                            {isSelected && (
                              <div className="absolute top-4 right-4">
                                <div className="w-6 h-6 rounded-full bg-teal-500 flex items-center justify-center">
                                  <CheckIcon className="w-4 h-4 text-white" />
                                </div>
                              </div>
                            )}

                            <div className="mb-4">
                              <h3 className="text-xl font-bold text-slate-900 dark:text-white">{plan.name}</h3>
                              {plan.price_usd && (
                                <p className="text-3xl font-bold text-slate-900 dark:text-white mt-2">
                                  ${plan.price_usd}
                                  <span className="text-sm font-normal text-slate-500 dark:text-slate-400">/month</span>
                                </p>
                              )}
                            </div>

                            <div className="space-y-3 mb-6">
                              <div className="flex items-center gap-2">
                                <CheckIcon className="w-5 h-5 text-teal-600 flex-shrink-0" />
                                <span className="text-sm text-slate-700 dark:text-slate-300">
                                  {plan.monthly_credits.toLocaleString()} credits/month
                                </span>
                              </div>
                              {plan.max_documents && (
                                <div className="flex items-center gap-2">
                                  <CheckIcon className="w-5 h-5 text-teal-600 flex-shrink-0" />
                                  <span className="text-sm text-slate-700 dark:text-slate-300">
                                    Up to {plan.max_documents.toLocaleString()} documents
                                  </span>
                                </div>
                              )}
                              {plan.max_editors && (
                                <div className="flex items-center gap-2">
                                  <CheckIcon className="w-5 h-5 text-teal-600 flex-shrink-0" />
                                  <span className="text-sm text-slate-700 dark:text-slate-300">
                                    Up to {plan.max_editors} editors per document
                                  </span>
                                </div>
                              )}
                              {plan.latex_time_limit === null && (
                                <div className="flex items-center gap-2">
                                  <CheckIcon className="w-5 h-5 text-teal-600 flex-shrink-0" />
                                  <span className="text-sm text-slate-700 dark:text-slate-300">
                                    Unlimited LaTeX compilation time
                                  </span>
                                </div>
                              )}
                            </div>

                            {isCurrent ? (
                              <button
                                disabled
                                className="w-full px-4 py-2 bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400 rounded-lg font-medium cursor-not-allowed"
                              >
                                Current Plan
                              </button>
                            ) : (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setSelectedPlan(key as 'student' | 'researcher')
                                  handleSubscribe()
                                }}
                                disabled={createSubscriptionMutation.isPending}
                                className={clsx(
                                  'w-full px-4 py-2 rounded-lg font-medium transition-colors',
                                  isSelected
                                    ? 'bg-teal-600 hover:bg-teal-700 text-white'
                                    : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-300 dark:hover:bg-slate-600',
                                  createSubscriptionMutation.isPending && 'opacity-50 cursor-not-allowed'
                                )}
                              >
                                {createSubscriptionMutation.isPending
                                  ? 'Processing...'
                                  : isUpgrade
                                  ? 'Upgrade'
                                  : 'Subscribe'}
                              </button>
                            )}
                          </div>
                        )
                      })}
                </div>
              )}

              {/* Simulation Notice */}
              <div className="mt-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                <p className="text-sm text-amber-800 dark:text-amber-300">
                  <strong>Note:</strong> Subscription is currently simulated. Payment integration will be added in a
                  future update.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </AnimatePresence>
  )
}

